import {
    Post,
    PostPreviewInfo,
    ActiveDaysRequest,
    PostsFilterDtoIn,
    CopyPost,
    PostActiveDaysRequest,
    BehaviorEnum
} from "@/services/posts/types";
import api from "@/services/api";
import { Rss, RssContentDto } from "./postTypes/rss";
import i18n from "@/i18n";
import moment from "moment";
import { Macros, MacrosCountInEnum, MacrosOptions, MacrosTypeEnum, fixedHtmlData } from "./postTypes/fixedhtml";

export async function getPostsByChannelId(id: string): Promise<Array<Post>> {
    return new Promise((resolve, reject) => {
        api.get(`/posts/channel/${id}`).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function getPost(id: string): Promise<Post> {
    return new Promise((resolve, reject) => {
        api.get(`/posts/${id}`).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function getPostPreviewInfo(id: string): Promise<PostPreviewInfo> {
    return new Promise((resolve, reject) => {
        api.get(`/posts/GetPostPreviewInfo/${id}`).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function createPost(item: Post): Promise<string> {
    return new Promise((resolve, reject) => {
        api.post("/posts/", item).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function updatePost(item: Post): Promise<void> {
    return new Promise((resolve, reject) => {
        api.put("/posts/", item).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function deletePost(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
        api.delete(`/posts/${id}`).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function copyPost(copyItem: CopyPost): Promise<void> {
    return new Promise((resolve, reject) => {
        api.post("/posts/copy", copyItem).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function disablePost(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
        api.post(`/posts/disable/${id}`, null).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function enablePost(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
        api.post(`/posts/enable/${id}`, null).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function swapPostOrder(itemOrder: any): Promise<void> {
    return new Promise((resolve, reject) => {
        api.post("/posts/swap/", itemOrder).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function getActiveDays(model: ActiveDaysRequest): Promise<Array<string>> {
    return new Promise((resolve, reject) => {
        api.post("/posts/activeDays", model).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function getPostActiveDays(model: PostActiveDaysRequest): Promise<Array<string>> {
    return new Promise((resolve, reject) => {
        api.post("/posts/postActiveDays", model).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function getFilteredPosts(model: PostsFilterDtoIn): Promise<Array<Post>> {
    return new Promise((resolve, reject) => {
        api.post("/posts/filter", model).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export async function getRssItems(model: RssContentDto): Promise<Rss> {
    return new Promise((resolve, reject) => {
        api.post("/posts/rss/content", model).then(
            response => {
                resolve(response.data);
            },
            error => {
                reject(error);
            }
        );
    });
}

export function FormatDate(dateTime: string) {
    if (dateTime) {
        if (moment(dateTime).format("YYYY") === "9999") {
            return "never-ending";
        } else {
            return i18n.d(new Date(dateTime), "short");
        }
    }
    return "";
}

export function LocalizeAllday(value: string) {
    return value === "Allday" ? i18n.t("common.allday") : value;
}

export function LocalizeType(value: string) {
    return i18n.t("posts." + CapitalizeFirstLetter(value));
}

export function CapitalizeFirstLetter(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function CalculateShowtime(val: any) {
    const type = val.postType.toLowerCase();
    if (type === "rss") return (val.duration * (val.rankEnd - val.rankStart + 1)).toString();
    if (type === "video") return "--";
    return val.duration;
}

// Preview has to be a string because it gets parsed as a string by the runtime compiler.
export function getVueTemplateAndData(templateString: string, editMode = true) {
    const div = document.createElement("div");
    div.innerHTML = templateString;

    const dataArr: fixedHtmlData[] = [];

    const allElementsWithStyleAttribute = div.querySelectorAll<HTMLElement>("[style]");

    allElementsWithStyleAttribute.forEach(element => {
        const style = element.style.cssText.toLowerCase();
        element.style.cssText = style;
    });

    const elementsWithId = div.querySelectorAll<HTMLElement>("[data-vs-id]");

    elementsWithId.forEach(element => {
        const id = element.getAttribute("data-vs-id");
        const style = element.style.cssText;
        const classes = element.classList;
        const typeAttribute = element.getAttribute("data-vs-type");
        const type = typeAttribute ? typeAttribute.toLowerCase() : null;

        const data = {} as fixedHtmlData;
        data.id = id || "";

        // Map data with falsy fallbacks.
        if (type === "text" || type === "formattext") {
            data.text = element.innerHTML || "";
            data.preventOverflow = element.getAttribute("data-vs-preventoverflow") || "true";
        }

        if (type === "image") {
            data.src = element.getAttribute("src") || "";
            data.behavior = BehaviorEnum.adjust;
            data.adjustOnly = element.getAttribute("data-vs-adjustonly") || "false";
        }

        if (type === "formattext") {
            data.toolbar = element.getAttribute("data-vs-toolbar") || "";
            data.fontSizes = element.getAttribute("data-vs-fontsizes") || "";
        }

        if (type === "macros") {
            const macroTypeValue = (element.getAttribute("data-vs-macrostype") || "").toLowerCase() as MacrosTypeEnum;
            const macroCountinValue = (element.getAttribute("data-vs-macroscountin") || "").toLowerCase() as MacrosCountInEnum;
            // Check for valid MacrosEnums
            const isValidMacroType = (macroType: MacrosTypeEnum) => Object.values(MacrosTypeEnum).some((value) => value.toLowerCase() === macroType.toLowerCase());
            const isValidMacroCountIn = (macroCountIn: MacrosCountInEnum) => Object.values(MacrosCountInEnum).some((value) => value.toLowerCase() === macroCountIn.toLowerCase());
            data.macros = {
                type: isValidMacroType(macroTypeValue) ? macroTypeValue : MacrosTypeEnum.countUp,
                countIn: isValidMacroCountIn(macroCountinValue) ? macroCountinValue : MacrosCountInEnum.days,
                macrosOptions: {
                    type: !isValidMacroType(macroTypeValue),
                    countIn: !isValidMacroCountIn(macroCountinValue)
                } as MacrosOptions
            } as Macros;
        }

        if (type === "qrcode") {
            data.qrCodeValue = element.getAttribute("value") || "";
            const qrcodeSize = parseInt(element.getAttribute("size") || "75");
            data.qrCodeSize = Math.max(qrcodeSize, 75).toString();
            const quitezone = parseInt(element.getAttribute("quitezone") || "2");
            data.qrCodeQuiteZone = Math.max(quitezone, 2).toString();
        }
        dataArr.push(data);

        if (editMode) element.outerHTML = `<${type}componentform :fileuploadinprogress="fileuploadinprogress" class='fixed-html-component ${classes}' vsid='${id}' style='${style}' styles='${style}'></${type}componentform>`;
        else element.outerHTML = `<${type}component class='${classes}' vsid='${id}' style='${style}'></${type}component>`;
    });

    return { template: div.outerHTML, data: dataArr };
}

export function saveSelection(containerEl: any) {
    var range = (window as any).getSelection().getRangeAt(0);
    var preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(containerEl);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    var start = preSelectionRange.toString().length;

    return {
        start: start,
        end: start + range.toString().length
    };
};

export function restoreSelection(containerEl: any, savedSel: any) {
    var charIndex = 0;
    var range = document.createRange();
    range.setStart(containerEl, 0);
    range.collapse(true);
    var nodeStack = [containerEl];
    var node;
    var foundStart = false;
    var stop = false;

    while (!stop && (node = nodeStack.pop())) {
        if (node.nodeType === 3) {
            var nextCharIndex = charIndex + node.length;
            if (!foundStart && savedSel.start >= charIndex && savedSel.start <= nextCharIndex) {
                range.setStart(node, savedSel.start - charIndex);
                foundStart = true;
            }
            if (foundStart && savedSel.end >= charIndex && savedSel.end <= nextCharIndex) {
                range.setEnd(node, savedSel.end - charIndex);
                stop = true;
            }
            charIndex = nextCharIndex;
        } else {
            var i = node.childNodes.length;
            while (i--) {
                nodeStack.push(node.childNodes[i]);
            }
        }
    }

    var sel: any = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
}
